import React, { useEffect, useMemo, useState } from 'react';
import PageLoader from '../PageLoader';
import { fetchSubscriptionPlans } from '../../services/subscription';
import { axiosInstanceV2 } from '../../services/axios';
import { toast } from 'react-toastify';
import { usePayment } from '../../hooks/usePayment';
import { useDispatch, useSelector } from 'react-redux';
import PricingCard from '../../pages/subscriptionPlan/components/PricingCard';
import { useNavigate } from 'react-router-dom';
import { setBillingType, setPlan } from '../../store/reducers/subscriptionSlice/subscriptionSlice';

function PricingModal({ isOpen, handleClose, }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { openSubscriptionCheckout } = usePayment();
  const subscription = useSelector((state) => state?.userInfo?.userInfo?.data?.subscription)
  const isFreeUser = subscription?.is_free;
  const is_recurring = subscription?.is_recurring
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [billing, setBilling] = useState(subscription?.plan?.period ?? 'monthly');
  const [confirmModal, setConfirmModal] = useState(null);
  const [processing, setProcessing] = useState(false);


  const getPlans = async () => {
    try {
      setLoading(true);
      const response = await fetchSubscriptionPlans();
      setPlans(response.plans);
    } catch (error) {
      toast.error('Failed to fetch subscription plans');
      setPlans([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) getPlans();
  }, [isOpen]);

  // handle plan selection
  const handleSelectPlan = (
    plan,
    billingDetails,
    type
  ) => {
    setConfirmModal({
      plan,
      billingDetails,
      type,
    });
  };

  // confirm upgrade 
  const handleConfirmPlanChange = async () => {
    setProcessing(true);
    const { plan, billingDetails } = confirmModal;

    console.log({ isFreeUser })
    if (isFreeUser) {
      dispatch(setPlan(plan));
      dispatch(setBillingType(billing));
      setConfirmModal(null);
      handleClose();
      navigate("/checkout");
      setProcessing(false);
      return;
    }

    let result = null;

    try {
      const authToken = localStorage.getItem('authToken');
      const response = await axiosInstanceV2.post(
        '/subscription/change-plan',
        {
          razorpay_plan_id: billingDetails.razorpay_plan_id,
          billing_period: billingDetails.period,
          is_recurring: is_recurring,
        },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );

      result = response.data.data;

      if (result.type === 'upgrade') {
        try {
          await openSubscriptionCheckout({
            subscription: result.subscription,
            billingDetails: result.billing_details,
            paymentMethod: subscription?.payment_details?.method,
          });
        } catch (checkoutErr) {
          // CHANGED: distinguish "backend call succeeded, Razorpay checkout failed"
          // from a plan-change API failure — different problem, different message
          console.error('Checkout error:', checkoutErr);
          toast.error('Payment window failed to open. Please try again.');
          return; // keep modal open, don't run success cleanup below
        }
      }

      if (result.type === 'downgrade_scheduled') {
        toast.success(
          `Downgrade to ${plan.name} scheduled for ${new Date(result.effective_date).toLocaleDateString()}`
        );
      }

      // CHANGED: only close the modal / reload on confirmed success
      setConfirmModal(null);
      handleClose();
      if (result.type === 'downgrade_scheduled') {
        window.location.reload();
      }

    } catch (error) {
      console.error('Plan change error:', error);

      // CHANGED: differentiate error types instead of one generic message for everything
      const status = error?.response?.status;
      const backendMessage = error?.response?.data?.message;

      if (!error?.response) {
        toast.error('Network error — check your connection and try again.');
      } else if (status === 401) {
        toast.error('Your session has expired. Please log in again.');
      } else {
        toast.error(backendMessage ?? 'Failed to update subscription. Please try again.');
      }
      // CHANGED: modal stays open on failure — no setConfirmModal(null) here,
      // so the user can retry without reselecting the plan
    } finally {
      setProcessing(false); // CHANGED: this alone in finally now — no result access here
    }
  };

  const filteredPlans = useMemo(() => {
    return plans
      .filter(plan => plan.key !== "free")
      .filter(plan =>
        plan.billing?.some(
          billingPlan => billingPlan.period === billing
        )
      );
  }, [plans, billing]);

  useEffect(() => {
    if (!isOpen) return;

    setBilling(subscription?.plan?.period || "monthly");
  }, [isOpen, subscription]);

  if (!isOpen) return null;

  return (
    <>
      <div
        className="modal fade show d-block"
        tabIndex="-1"
        aria-modal="true"
        role="dialog"
        style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
      >
        <div className="modal-dialog modal-dialog-top modal-xl">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">
                Change Subscription Plan
              </h5>
              <button type="button" className="btn-close" onClick={handleClose} />
            </div>

            <div className='d-flex justify-content-center align-items-center py-4'>
              <div className="billing-toggle">
                <button
                  className={billing === 'monthly' ? 'active' : ''}
                  onClick={() => setBilling('monthly')}
                >
                  Monthly
                </button>
                <button
                  className={billing === 'yearly' ? 'active' : ''}
                  onClick={() => setBilling('yearly')}
                >
                  Yearly
                </button>
              </div>
            </div>

            <div className="modal-body w-100">
              {loading ? (
                <PageLoader />
              ) : filteredPlans.length === 0 ? (
                <div className="text-center py-5">
                  <h5>No Plans Available</h5>
                  <p className="text-muted mb-0">
                    No {billing} subscription plans are currently available.
                  </p>
                </div>
              ) : (
                <div className="pricing-grid">
                  {filteredPlans.map((plan) => {
                    const billingDetails = plan.billing.find(
                      (item) => item.period === billing
                    );

                    return (
                      <PricingCard
                        key={plan._id || plan.key}
                        plan={plan}
                        billing={billing}
                        billingDetails={billingDetails}
                        currentPlanKey={subscription?.plan?.key}
                        currentRazorpayPlanId={subscription?.plan?.razorpay_plan_id}
                        changeType={plan.change_type}
                        onSelectPlan={handleSelectPlan}
                      />
                    );
                  })}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn-cancel" onClick={handleClose}>
                Close
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* confirmation modal */}
      {confirmModal && (
        <div
          className="modal fade show d-block"
          style={{ backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 1060 }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {isFreeUser
                    ? "Subscribe to Plan"
                    : confirmModal.type === "upgrade"
                      ? "Confirm Upgrade"
                      : "Confirm Downgrade"}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setConfirmModal(null)}
                />
              </div>

              <div className="modal-body">
                <div className="modal-body">
                  {isFreeUser ? (
                    <p>
                      You're subscribing to the
                      <strong> {confirmModal.plan.name}</strong> plan.
                      Click Continue to provide your billing details before completing payment.
                    </p>
                  ) : confirmModal.type === "upgrade" ? (
                    <p>
                      After successful payment, your
                      <strong> {confirmModal.plan.name}</strong> plan will become active
                      immediately.
                    </p>
                  ) : (
                    <p>
                      You're upgrading to <strong>{confirmModal.plan.name}</strong> ({confirmModal.billingDetails.period}) for <strong>
                        ₹
                        {confirmModal.billingDetails.amount_in_rupees.toFixed(2)}
                      </strong>.
                    </p>
                  )}
                </div>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn-cancel"
                  onClick={() => setConfirmModal(null)}
                >
                  Cancel
                </button>
                <button
                  disabled={processing}
                  type="button"
                  className="btn-primary-modal"
                  onClick={handleConfirmPlanChange}
                >
                  {isFreeUser
                    ? "Continue"
                    : confirmModal.type === "upgrade"
                      ? "Proceed to Payment"
                      : "Confirm Downgrade"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default PricingModal;